import React from 'react';
import Input from '../../shared/Input';
import SearchSelect from '../../shared/SearchSelect';
import { XCircle } from 'lucide-react';

const Step2ContactLocation = ({
  formData,
  errors,
  countries,
  countriesLoading,
  countriesError,
  handleInputChange,
  handleSocialMediaChange,
  removeSocialMediaPlatform,
  addSocialMediaPlatform,
  showAddPlatformModal,
  setShowAddPlatformModal,
  newPlatformName,
  setNewPlatformName,
  handleAddPlatform,
  handleCancelAddPlatform
}) => {
  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2 sm:mb-4">Contact & Location</h3>
        <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
          Provide your business contact information and service area.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <Input
          label="Business Phone *"
          type="tel"
          value={formData.business_phone}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '' || /^[\d\s()\-+]+$/.test(value)) {
              handleInputChange('business_phone', value);
            }
          }}
          onKeyDown={(e) => {
            if (!/[\d\s()\-+]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onPaste={(e) => {
            const paste = e.clipboardData.getData('text');
            if (!/^[\d\s()\-+]+$/.test(paste)) {
              e.preventDefault();
            }
          }}
          error={errors.business_phone}
          placeholder="(555) 123-4567"
          className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />

        <Input
          label="Business Email *"
          type="email"
          value={formData.business_email}
          onChange={(e) => handleInputChange('business_email', e.target.value)}
          error={errors.business_email}
          placeholder="business@example.com"
        />
      </div>

      <Input
        label="Website"
        value={formData.website}
        onChange={(e) => handleInputChange('website', e.target.value)}
        placeholder="https://yourwebsite.com"
      />

      {/* Social Media Section */}
      <div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 sm:mb-4 gap-2">
          <h4 className="text-sm sm:text-md font-medium text-gray-900">Social Media (Optional)</h4>
          <button
            type="button"
            onClick={addSocialMediaPlatform}
            className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            + Add Platform
          </button>
        </div>
        
        {Object.keys(formData.social_media).length === 0 ? (
          <div className="text-center py-6 sm:py-8 text-gray-500">
            <p className="text-xs sm:text-sm">No social media platforms added yet.</p>
            <p className="text-xs">Click "Add Platform" to get started.</p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {Object.entries(formData.social_media).map(([platform, url]) => (
              <div key={platform} className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                <div className="flex-1 w-full">
                  <Input
                    label={platform.charAt(0).toUpperCase() + platform.slice(1)}
                    value={url}
                    onChange={(e) => handleSocialMediaChange(platform, e.target.value)}
                    placeholder={`https://${platform}.com/yourpage`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeSocialMediaPlatform(platform)}
                  className="text-red-600 hover:text-red-700 p-2 self-start sm:self-center"
                  title="Remove platform"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
          Business Address *
        </label>
        <textarea
          value={formData.address}
          onChange={(e) => handleInputChange('address', e.target.value)}
          rows={2}
          className={`w-full px-3 py-2 text-sm sm:text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.address ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Street address, suite, etc."
        />
        {errors.address && (
          <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.address}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <Input
          label="City *"
          value={formData.city}
          onChange={(e) => handleInputChange('city', e.target.value)}
          error={errors.city}
          placeholder="City"
        />

        <Input
          label="State *"
          value={formData.state}
          onChange={(e) => handleInputChange('state', e.target.value)}
          error={errors.state}
          placeholder="State"
        />

        <Input
          label="ZIP Code *"
          value={formData.zip_code}
          onChange={(e) => handleInputChange('zip_code', e.target.value)}
          error={errors.zip_code}
          placeholder="12345"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div>
          <SearchSelect
            label="Country"
            value={formData.country}
            onChange={(value) => handleInputChange('country', value)}
            options={countries.map(country => ({
              value: country.code,
              label: `${country.emoji} ${country.name}`,
              searchTerm: `${country.name} ${country.code} ${country.dial_code}`
            }))}
            placeholder="Select country"
            loading={countriesLoading}
            emptyMessage={countriesError ? "Failed to load countries" : "No countries found"}
            searchable={true}
          />
        </div>
      </div>
    </div>
  );
};

export default Step2ContactLocation;


